/**
 * The upload ingest's five stages, drawn from the run row so they survive a
 * refresh. Same shape as `FileProcessing`.
 *
 * `verify` is shown rather than folded into `upload` because it is the stage
 * that earns the file's status: it only becomes mappable once the Parquet has
 * actually been read back.
 */

import { useEffect, useRef } from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { useFileIngestQuery } from '../../queries/intake';
import { Card, Note } from '../../shared/ui';
import StageProgress from '../../shared/components/StageProgress';
import type { StageStatus } from '../../shared/components/StageProgress/types';
import { count } from '../../utils/format';

/** `python/app/application/ingest.py:STAGES`, held here so unreached stages can be drawn. */
const STAGES = [
	{ key: 'uploaded', label: 'File uploaded' },
	{ key: 'read', label: 'Reading sheets' },
	{ key: 'convert', label: 'Saving a working copy' },
	{ key: 'upload', label: 'Storing the working copy' },
	{ key: 'verify', label: 'Checking the copy' },
] as const;

/** Sub-second work reads as "0.4s" rather than "0s". */
function took(ms: number | null): string {
	if (ms === null) return '';
	if (ms < 10_000) return `${(ms / 1000).toFixed(1)}s`;
	return `${Math.round(ms / 1000)}s`;
}

export default function IngestProgress({
	fileId,
	onRetry,
	onSettled,
}: {
	fileId: string;
	onRetry?: () => void;
	/** Fired once when the run settles. The wizard uses it to move a row out of
	 *  "reading": the upload call now returns before the work starts. */
	onSettled?: (status: 'COMPLETED' | 'FAILED') => void;
}) {
	const ingest = useFileIngestQuery(fileId);
	const run = ingest.data?.run ?? null;

	/* Once per run, not once per poll. */
	const announced = useRef<string | null>(null);
	/* `run.status` already reads FAILED for a stale run; the server decides that. */
	const status = run?.status;
	useEffect(() => {
		if (!run || !onSettled) return;
		if (status !== 'COMPLETED' && status !== 'FAILED') return;
		if (announced.current === run.runId) return;
		announced.current = run.runId;
		onSettled(status);
	}, [run, status, onSettled]);

	/* A file uploaded before ingest became a run never had one. */
	if (!run) return null;

	const byStage = new Map(run.steps.map((step) => [step.stage, step]));
	const failed = run.status === 'FAILED';
	const done = run.status === 'COMPLETED';
	const failedStage = run.steps.find(
		(step) => step.status === 'FAILED',
	)?.stage;

	/* A number a person can watch move, not just five badges that each flip
     once. Half a step's weight for "running" — the stage IS in progress, but
     counting it as a full step would make the bar jump the instant it
     starts and then sit still until the next one does. */
	const doneCount = STAGES.reduce((total, { key }) => {
		const state = byStage.get(key)?.status;
		if (state === 'DONE') return total + 1;
		if (state === 'RUNNING') return total + 0.5;
		return total;
	}, 0);
	const progressPct = failed
		? Math.round((doneCount / STAGES.length) * 100)
		: done
			? 100
			: Math.round((doneCount / STAGES.length) * 100);

	return (
		<Card
			title="Reading the file"
			hint={
				done
					? `${count(run.rowCount ?? 0)} rows · ${run.columnCount ?? 0} columns · ${took(
							run.totalMs,
						)}`
					: failed
						? undefined
						: 'Updates automatically'
			}
		>
			<Box className="stack2">
				{failed && (
					<Note
						tone="danger"
						title={
							failedStage
								? `Stopped while it was ${
										STAGES.find(
											(s) => s.key === failedStage,
										)?.label.toLowerCase() ?? failedStage
									}`
								: 'Reading the file failed'
						}
						row
					>
						{run.errorMessage ?? 'No message was recorded.'}
						{onRetry && (
							<Button
								variant="outlined"
								size="small"
								onClick={onRetry}
							>
								Try again
							</Button>
						)}
					</Note>
				)}

				{!done && !failed && (
					<Typography variant="body2" className="small mut">
						This keeps going if you leave the page — nothing is lost
						by coming back to it.
					</Typography>
				)}

				<LinearProgress
					variant="determinate"
					value={progressPct}
					color={failed ? 'error' : done ? 'success' : 'primary'}
				/>

				<StageProgress
					stages={STAGES}
					statusOf={(key) =>
						(byStage.get(key)?.status ?? 'PENDING') as StageStatus
					}
					detailOf={(key) => {
						const step = byStage.get(key);
						if (!step) return null;
						return [
							step.detail,
							step.durationMs != null
								? took(step.durationMs)
								: null,
						]
							.filter(Boolean)
							.join(' · ');
					}}
				/>
			</Box>
		</Card>
	);
}
